import React, { useCallback, useState } from 'react';
import { formatNumber } from 'humanize-plus';
import { useApolloClient } from '@apollo/client';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';

import Button, { BUTTON_TYPES, BUTTON_WIDTHS } from 'buyside/components/presentation/Button/button';
import IdeaUpdateForm from 'buyside/components/presentation/IdeaShowPage/AuthorActions/ideaUpdateForm';
import Modal from 'buyside/components/presentation/Modal/modal';

import {
  IdeaAttachment,
  IdeaCoreDocument,
  IdeaCoreQuery,
  IdeaPositionsDocument,
  IdeaPositionsPricingDocument,
  IdeaPositionsPricingQuery,
  IdeaPositionsQuery
} from 'interfaces/graphql-typings';

const IdeaUpdateModal: React.FC<{
  analyticsTag: string;
  attachments?: IdeaAttachment[];
  body?: string;
  catalysts?: string[];
  closeIdea: boolean;
  createdAt?: string;
  faIcon?: IconDefinition;
  ideaId: string;
  ideaUpdateId?: string;
  impact?: string;
  main?: boolean;
  modalText: string;
  priceTarget?: string;
  returnTarget?: string;
  situation?: string;
  targetAllocation?: string;
  timeframe?: string;
}> = ({
  analyticsTag,
  attachments,
  body,
  catalysts,
  closeIdea,
  createdAt,
  faIcon,
  ideaId,
  ideaUpdateId,
  impact,
  main = false,
  modalText,
  priceTarget,
  returnTarget,
  situation,
  targetAllocation,
  timeframe
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const client = useApolloClient();

  const cachedIdea = client.readQuery<IdeaCoreQuery>({
    query: IdeaCoreDocument,
    variables: { ideaId }
  })?.idea;

  const cachedPosIdea = client.readQuery<IdeaPositionsQuery>({
    query: IdeaPositionsDocument,
    variables: { ideaId }
  })?.idea;

  const cachedPosPricingIdea = client.readQuery<IdeaPositionsPricingQuery>({
    query: IdeaPositionsPricingDocument,
    variables: { ideaId }
  })?.idea;

  const renderOpenModalButton = useCallback(
    () => (
      <Button
        analyticsTag={analyticsTag}
        faIcon={faIcon}
        onClick={() => setIsModalOpen(true)}
        text={modalText}
        type={main ? BUTTON_TYPES.PRIMARY : BUTTON_TYPES.LINK}
        width={main ? BUTTON_WIDTHS.FULL : undefined}
      />
    ),
    [analyticsTag, faIcon, main, modalText]
  );

  if (!cachedIdea || !cachedPosIdea || !cachedPosPricingIdea)
    return (
      <Button
        isLoading
        text={modalText}
        type={main ? BUTTON_TYPES.PRIMARY : BUTTON_TYPES.LINK}
        width={main ? BUTTON_WIDTHS.FULL : undefined}
      />
    );

  const position = cachedPosIdea.positions[0];
  const positionPricing = cachedPosPricingIdea.positions[0];
  const currencyCode = position.asset?.currencyCode || null;
  const direction = position.direction;
  const latestPrice = positionPricing.latestPrice || null;
  const isPairTrade = Boolean(cachedPosIdea.positions.length > 1);
  const openingPrice = positionPricing.adjOpeningPrice || null;
  const ideaPriceTarget =
    Boolean(position.priceTarget) || position.priceTarget === 0
      ? formatNumber(position.priceTarget || 0, 2)
      : null;
  const ideaReturnTarget = cachedIdea.returnTarget
    ? formatNumber(cachedIdea.returnTarget, 2)
    : null;

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      renderOpenModalButton={renderOpenModalButton}
      size="lg"
      title={modalText}
    >
      <IdeaUpdateForm
        attachments={attachments || []}
        body={body || ''}
        catalysts={catalysts || cachedIdea.catalystNames || []}
        closeIdea={closeIdea}
        closeModal={() => setIsModalOpen(false)}
        createdAt={createdAt || cachedIdea.createdAt}
        currencyCode={currencyCode}
        direction={direction}
        ideaId={ideaId}
        ideaUpdateId={ideaUpdateId}
        impact={impact || ''}
        isNewUpdate={modalText !== 'Edit Update'}
        isPairTrade={isPairTrade}
        latestPrice={latestPrice}
        openingPrice={openingPrice}
        priceTarget={priceTarget || ideaPriceTarget}
        returnTarget={returnTarget || ideaReturnTarget}
        situation={situation || cachedIdea.situationUnformatted || ''}
        targetAllocation={targetAllocation || cachedIdea.targetAllocation || ''}
        timeframe={timeframe || cachedIdea.timeframe || ''}
      />
    </Modal>
  );
};

export default IdeaUpdateModal;
